import { useCallback, useEffect, useRef, useState } from "react";

import { useDashboardRealtime } from "../../hooks/useDashboardRealtime";

// ---------------------------------------------------------------------------
// Hook: useFleetActivitySeries
// Maintains ring-buffer time series (one sample per second, ~30 samples) for
// Fleet-wide burn rate, tokens/min, and cache-hit % — rendered as sparklines
// In the bento header. Reads from the same instance_activity token_delta and
// Cache_stats events as the fleet header.
// ---------------------------------------------------------------------------

const WINDOW_MS = 60_000;
const MS_PER_HOUR = 3_600_000;
const SAMPLE_INTERVAL_MS = 1500;
const MAX_SAMPLES = 30;

interface TokenEntry {
  inputTokens: number;
  outputTokens: number;
  costDeltaUsd: number;
  timestampMs: number;
}

interface CacheEntry {
  hitPct: number;
  timestampMs: number;
}

interface GrowthEntry {
  growthTokens: number;
  timestampMs: number;
}

export interface FleetActivitySeries {
  burnRateUsdPerHr: number | null;
  burnRateSamples: number[];
  tokensPerMinSamples: number[];
  cacheHitSamples: number[];
  /** Persisted-context growth across the fleet, normalized to tokens/min over the same 60s window. The leading indicator of burn rate (every grown token = +1 in every future wake's input until compaction). */
  growthPerMinSamples: number[];
  growthPerMin: number | null;
}

export function useFleetActivitySeries(): FleetActivitySeries {
  const buffersRef = useRef<Map<string, TokenEntry[]>>(new Map());
  const cacheBufferRef = useRef<CacheEntry[]>([]);
  const growthBufferRef = useRef<GrowthEntry[]>([]);
  const [burnRateUsdPerHr, setBurnRateUsdPerHr] = useState<number | null>(null);
  const [burnRateSamples, setBurnRateSamples] = useState<number[]>([]);
  const [tokensPerMinSamples, setTokensPerMinSamples] = useState<number[]>([]);
  const [cacheHitSamples, setCacheHitSamples] = useState<number[]>([]);
  const [growthPerMinSamples, setGrowthPerMinSamples] = useState<number[]>([]);
  const [growthPerMin, setGrowthPerMin] = useState<number | null>(null);
  const { subscribe } = useDashboardRealtime();

  useEffect(() => {
    const unsubscribe = subscribe(
      (message) => {
        if (message.type !== "instance_activity") {
          return;
        }
        const { payload } = message;
        if (typeof payload.instance_id !== "string") {
          return;
        }
        if (payload.activity_type === "token_delta") {
          const { detail } = payload;
          const entry: TokenEntry = {
            costDeltaUsd:
              typeof detail.cost_delta_usd === "number"
                ? detail.cost_delta_usd
                : 0,
            inputTokens:
              typeof detail.input_tokens_delta === "number"
                ? detail.input_tokens_delta
                : 0,
            outputTokens:
              typeof detail.output_tokens_delta === "number"
                ? detail.output_tokens_delta
                : 0,
            timestampMs: message.timestamp,
          };
          const existing = buffersRef.current.get(payload.instance_id);
          if (existing) {
            existing.push(entry);
          } else {
            buffersRef.current.set(payload.instance_id, [entry]);
          }
        } else if (payload.activity_type === "cache_stats") {
          const { detail } = payload;
          const hitPct =
            typeof detail.cache_hit_pct === "number"
              ? detail.cache_hit_pct
              : null;
          if (hitPct !== null) {
            cacheBufferRef.current.push({
              hitPct,
              timestampMs: message.timestamp,
            });
            if (cacheBufferRef.current.length > 50) {
              cacheBufferRef.current.shift();
            }
          }
        } else if (payload.activity_type === "context_window") {
          const { detail } = payload;
          const growth =
            typeof detail.context_growth_this_wake === "number"
              ? detail.context_growth_this_wake
              : null;
          if (growth !== null && growth > 0) {
            growthBufferRef.current.push({
              growthTokens: growth,
              timestampMs: message.timestamp,
            });
          }
        }
      },
      { topic: "internalEventTopic" },
    );
    return unsubscribe;
  }, [subscribe]);

  const sample = useCallback(() => {
    const now = Date.now();
    const cutoff = now - WINDOW_MS;

    // Sustained fleet burn = (total cost across all instances over the full
    // 60s window) / 60s × 3600s. Dividing by the full window — not per-instance
    // Span with a 5s floor — keeps a single $0.04 LLM call from extrapolating
    // To $28/hr per instance and summing into spike-noise across the fleet.
    // Tradeoff: ramp-up moments under-report briefly until the window fills.
    let fleetCostInWindow = 0;
    let tokensInWindow = 0;
    let hasAny = false;

    for (const [id, buffer] of buffersRef.current) {
      const pruned = buffer.filter((e) => e.timestampMs >= cutoff);
      buffersRef.current.set(id, pruned);
      if (pruned.length === 0) {
        continue;
      }
      hasAny = true;
      for (const e of pruned) {
        fleetCostInWindow += e.costDeltaUsd;
        tokensInWindow += e.inputTokens + e.outputTokens;
      }
    }

    const currentBurn = hasAny
      ? (fleetCostInWindow / WINDOW_MS) * MS_PER_HOUR
      : 0;
    setBurnRateUsdPerHr(hasAny ? currentBurn : null);
    setBurnRateSamples((prev) => {
      const next = [...prev, currentBurn];
      return next.length > MAX_SAMPLES ? next.slice(-MAX_SAMPLES) : next;
    });

    const tokensPerMin = tokensInWindow;
    setTokensPerMinSamples((prev) => {
      const next = [...prev, tokensPerMin];
      return next.length > MAX_SAMPLES ? next.slice(-MAX_SAMPLES) : next;
    });

    // Cache hit %: rolling mean of the last N cache_stats events (any instance).
    const recentCache = cacheBufferRef.current.slice(-5);
    const meanHitPct =
      recentCache.length === 0
        ? 0
        : recentCache.reduce((s, e) => s + e.hitPct, 0) / recentCache.length;
    setCacheHitSamples((prev) => {
      const next = [...prev, meanHitPct];
      return next.length > MAX_SAMPLES ? next.slice(-MAX_SAMPLES) : next;
    });

    // Growth rate: tokens-of-context-growth across the fleet in the last 60s,
    // Normalized to per-minute. Same windowing rationale as the burn rate —
    // Divide by full window for honest sustained signal, not per-instance bursts.
    growthBufferRef.current = growthBufferRef.current.filter(
      (e) => e.timestampMs >= cutoff,
    );
    const fleetGrowthInWindow = growthBufferRef.current.reduce(
      (s, e) => s + e.growthTokens,
      0,
    );
    const hasGrowth = growthBufferRef.current.length > 0;
    const currentGrowthPerMin = hasGrowth
      ? (fleetGrowthInWindow / WINDOW_MS) * 60_000
      : 0;
    setGrowthPerMin(hasGrowth ? currentGrowthPerMin : null);
    setGrowthPerMinSamples((prev) => {
      const next = [...prev, currentGrowthPerMin];
      return next.length > MAX_SAMPLES ? next.slice(-MAX_SAMPLES) : next;
    });
  }, []);

  useEffect(() => {
    const id = window.setInterval(sample, SAMPLE_INTERVAL_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [sample]);

  return {
    burnRateSamples,
    burnRateUsdPerHr,
    cacheHitSamples,
    growthPerMin,
    growthPerMinSamples,
    tokensPerMinSamples,
  };
}
